/**
 * The Status cell of a document's row.
 *
 * Expects the document's row as the api sends it.
 *
 * Renders the badge that says where the document stands and, under it, the sentence that says what
 * to do about it where there is one. Every sentence and every badge class comes from
 * documentStatusWording in wording.ts, so this component decides nothing about what a row says.
 *
 * h-auto and max-w-full are what let the badge wrap inside its cell. daisyUI's .badge fixes the
 * height at 1.25rem and keeps its text on one line, and the Status column is a fixed share of the
 * documents table (DocumentsTable.tsx), so a sentence such as 'Same file already attached' is wider
 * than the column and ran into the column beside it. The badge is also an inline-flex box that
 * sizes itself to its text: on a narrow table 'Needs a document type' measured 94 pixels inside a
 * cell 60 pixels wide and ran over the Actions column.
 */

import type { TaxDocumentListItem } from '@shared/types/taxProfile';
import { documentStatusWording } from '@/pages/TaxProfile/wording';

export default function DocumentStatus({
  document,
}: {
  document: TaxDocumentListItem;
}) {
  const wording = documentStatusWording(document);

  return (
    <div className='flex flex-col gap-1'>
      <span
        className={`badge ${wording.badgeClass} whitespace-normal h-auto max-w-full py-0.5 leading-tight text-left`}
      >
        {wording.text}
      </span>
      {/* [overflow-wrap:anywhere] breaks a long name inside the sentence, such as the other
          client's name on a held document, rather than letting it run past the cell. */}
      {wording.why && (
        <span className='text-xs opacity-70 [overflow-wrap:anywhere]'>
          {wording.why}
        </span>
      )}
    </div>
  );
}
